import { ActionContext } from "vuex";
import * as fb from "../../firebase";
// import router from "../../router";

import {
  ExpiringPoints,
  GtrBonusStatusConfig,
  GtrBonusTypesConfig,
  GtrLifestyleBonus,
  PointsHistory,
  RankQualificationState,
  RankStatus,
  UPDATE_USER_EXPIRING_POINTS,
  UPDATE_USER_GTR_LIFESTYLE_BONUS,
  UPDATE_USER_POINTS_HISTORY,
  UPDATE_USER_RANK_REQUALIF,
} from "./RankQualificationInterfaces";
import {
  GtrBonusStatusMap,
  GtrBonusTypesMap,
} from "./RankQualificationLocalData";

const initialState: RankQualificationState = {
  bonusList: new Array<GtrLifestyleBonus>(),
  expiringPoints: new Array<ExpiringPoints>(),
  pointsHistory: new Array<PointsHistory>(),
  rankRequalification: new Map<string, Array<RankStatus>>(),
  gtrLifeBonusConfig: {
    status: GtrBonusStatusMap,
    type: GtrBonusTypesMap,
  },
};

const state: RankQualificationState = { ...initialState };
const mutations = {
  UPDATE_USER_GTR_LIFESTYLE_BONUS(
    state: RankQualificationState,
    bonusList: Array<GtrLifestyleBonus>
  ) {
    state.bonusList = bonusList;
  },
  UPDATE_USER_EXPIRING_POINTS(
    state: RankQualificationState,
    expiringPoints: Array<ExpiringPoints>
  ) {
    state.expiringPoints = expiringPoints;
  },
  UPDATE_USER_POINTS_HISTORY(
    state: RankQualificationState,
    pointsHistory: Array<PointsHistory>
  ) {
    state.pointsHistory = pointsHistory;
  },
  UPDATE_USER_RANK_REQUALIF(
    state: RankQualificationState,
    rankRequalification: Map<string, Array<RankStatus>>
  ) {
    state.rankRequalification = rankRequalification;
  },
  CLEAR_RANK_QUALIF_DATA(state: RankQualificationState) {
    state.bonusList = new Array<GtrLifestyleBonus>();
    state.expiringPoints = new Array<ExpiringPoints>();
    state.pointsHistory = new Array<PointsHistory>();
    state.rankRequalification = new Map<string, Array<RankStatus>>();
  },
};
const actions = {
  async doGetExpiringPoints(
    context: ActionContext<RankQualificationState, any>
  ) {
    const getExpiringPoints = fb.func.httpsCallable(
      "RankPoints-getExpiringPoints"
    );
    const response = await getExpiringPoints().then((response) => {
      if (response.data.OPCODE == "SUCCESS") {
        return response.data.data;
      } else {
        throw response.data.error;
      }
    });
    const expiringPoints = response.map((points: any) => {
      return {
        date: new Date(points.date),
        left: points.left,
        right: points.right,
        total: points.total,
      };
    });
    context.commit(UPDATE_USER_EXPIRING_POINTS, expiringPoints);
  },
  async doGetGtrLifeBonus(context: ActionContext<RankQualificationState, any>) {
    const getGtrLifeBonuses = fb.func.httpsCallable(
      "RankPoints-getGtrLifeBonuses"
    );
    const bonusList = await getGtrLifeBonuses().then((response) => {
      if (response.data.OPCODE == "SUCCESS") {
        return response.data.data;
      } else {
        throw response.data.error;
      }
    });
    context.commit(UPDATE_USER_GTR_LIFESTYLE_BONUS, bonusList);
  },
  async doGetPointsHistory(
    context: ActionContext<RankQualificationState, any>
  ) {
    // const username = await context.rootGetters["auth/getUsernamePK"];

    // CALL API
    const getPointsHistory = fb.func.httpsCallable(
      "RankPoints-getPointsHistory"
    );
    const response = await getPointsHistory().then((response) => {
      if (response.data.OPCODE == "SUCCESS") {
        return response.data.data;
      } else {
        throw response.data.error;
      }
    });

    const rankPointsHistory = new Map<string, Array<RankStatus>>();
    Object.keys(response).forEach((key: string) => {
      const rankStatus = response[key].map((rank: any) => {
        return {
          period: rank._period,
          status: rank.requalified,
        } as RankStatus;
      });
      rankPointsHistory.set(key, rankStatus);
    });

    // The ENTRY points rank — the first rung of the points ladder, where every
    // member's points history starts. This was hardcoded to `response["INFLUENCER"]`,
    // and the network reset re-mints that id (`INFLUENCER` -> `ELITE`, issue #561):
    // the key would simply stop existing, `?.` would swallow it, `|| []` would hand
    // back an empty array, and the points graph would render EMPTY with no error —
    // for everyone, from go-live on.
    //
    // Taken from the response instead of named. The backend returns the points ranks
    // ordered by ascending priority (`pointsRankIds`, RankUtils) and only includes
    // the ones with data; and its writers always cover the ladder from the BOTTOM up
    // to the member's next rank, so whenever there is any history at all, the lowest
    // key present is the entry rank. No id, nothing to update on go-live day.
    const entryPointsRank = Object.keys(response)[0];

    const pointsHistory =
      (response[entryPointsRank]?.map((data: any) => {
        return {
          period: data._period,
          points: data.maxPeriodPoints,
        } as PointsHistory;
      }) as Array<PointsHistory>) || new Array<PointsHistory>();

    context.commit(UPDATE_USER_POINTS_HISTORY, pointsHistory);
    context.commit(UPDATE_USER_RANK_REQUALIF, rankPointsHistory);
  },
};
const getters = {
  getExpiringPoints(state: RankQualificationState) {
    return state.expiringPoints;
  },
  getGtrLifeBonus(state: RankQualificationState) {
    return state.bonusList;
  },
  getGtrLifeBonusTypeConfig(
    state: RankQualificationState,
    getters: any,
    rootState: any,
    rootGetters: any
  ) {
    return (type: string): GtrBonusTypesConfig | undefined => {
      const config = state.gtrLifeBonusConfig.type.get(type);
      if (config) {
        return config;
      }

      // `gtrLifeBonus[<rankId>]` carries a rank id inside the type, and the map
      // lists the six of them one by one. The reset re-mints those ids
      // (`FINANCIAL-INF` -> `ROCKSTAR`, …, issue #561), so from go-live every GTR
      // Life bonus would miss the map, this getter would return `undefined`, and
      // the row would render with no label and no icon.
      //
      // Matched by SHAPE, with the rank's own name and icon pulled from the ranks
      // store — which is where they were copied from in the first place. So a new
      // rank needs no entry here, and the six above keep winning for the ids that
      // already have one (nothing rendering today changes).
      const rankId = /^gtrLifeBonus\[(.+)\]$/.exec(type)?.[1];
      if (rankId) {
        const rankName = rootGetters["ranks/rankName"](rankId);
        const rankIcon = rootGetters["ranks/rankIconClass"](rankId);
        return {
          code: type,
          // `rankName` is "" for a rank the store does not have (retired, or the
          // store not loaded yet). Falling back to the id keeps the row readable
          // instead of printing "Recalificación: ".
          label: `Recalificación: ${rankName || rankId}`,
          icon: rankIcon
            ? `${rankIcon} fa-fw text-pink`
            : "fas fa-fw fa-passport text-pink",
          // Llave para <rank-icon> (SVG inline). `null` cuando el rango no está en
          // el store → el render cae al `icon` webfont (pasaporte) de arriba.
          rankIconClass: rankIcon || null,
        };
      }

      return undefined;
    };
  },
  getGtrLifeBonusStatusConfig(state: RankQualificationState) {
    return (status: string): GtrBonusStatusConfig | undefined => {
      return state.gtrLifeBonusConfig.status.get(status);
    };
  },
  getPointsHistory(state: RankQualificationState) {
    return state.pointsHistory;
  },
  getRankRequalification(state: RankQualificationState) {
    return state.rankRequalification;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
